import { useTranslation } from "react-i18next";

import { IconButton, Tooltip, useAlert, useDialog } from "@digital-services/ui";

import { ApplicationJsonModel } from "@application/model/ApplicationJsonModel";
import { useDisassociateApplication } from "@organization/services/use-disassociate-organization-application";

import TrashIcon from "@icons/trash.svg?react";

interface Props {
  organizationId: string;
  application: ApplicationJsonModel;
  disabled?: boolean;
  afterCallback?: () => void;
}

export const RemoveApplicationAssociationButton = (props: Props) => {
  const { organizationId, application, disabled, afterCallback } = props;

  const { showDialog } = useDialog();
  const { notifySuccess, notifyError } = useAlert();

  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });
  const { t: tCommon } = useTranslation();

  const { remove } = useDisassociateApplication();

  function handleDelete(): void {
    showDialog({
      variant: "warning",
      header: t("delete.confirm.title"),
      content: t("delete.confirm.message"),
      confirmButton: tCommon("button.confirm.label"),
      onConfirm: async () => {
        await removeApplication();
      },
      cancelButton: tCommon("button.cancel.label"),
      onCancel: () => {
        /* Do nothing */
      },
      "data-testid": "delete-application-association"
    });
  }

  const removeApplication = async () => {
    await remove(organizationId, application.id)
      .then(response => {
        if (response instanceof Error) {
          notifyError({
            header: t("delete.error.message", {
              applicationName: application.name
            }),
            "data-testid": "delete-application-error-alert"
          });
        } else {
          notifySuccess({
            header: t("delete.success.message", {
              applicationName: application.name
            }),
            "data-testid": "delete-application-success-alert"
          });

          if (afterCallback) {
            afterCallback();
          }
        }
      })
      .catch(() => {
        notifyError({
          header: t("delete.error.message", {
            applicationName: application.name
          }),
          "data-testid": "delete-application-error-alert"
        });
      });
  };

  return (
    <Tooltip
      message={t("delete.tooltip", { applicationName: application.name })}
    >
      <IconButton
        onClick={handleDelete}
        data-testid={`delete-icon-button-${application.id}`}
        disabled={disabled}
      >
        <TrashIcon className="h-3 sm:h-4" />
      </IconButton>
    </Tooltip>
  );
};
